"use client";

import { Navigate, useNavigate } from "@tanstack/react-router";
import type { ReactNode } from "react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { authClient } from "@/lib/auth-client";
import { isSuperAdminFlag, isSuperAdminRole } from "@/lib/super-admin";

type AuthEmailAddress = {
	emailAddress: string;
};

type AuthUser = {
	id: string;
	fullName: string;
	firstName: string | null;
	lastName: string | null;
	emailAddresses: AuthEmailAddress[];
	imageUrl: string | null;
	publicMetadata: { admin?: boolean };
	privateMetadata: { admin?: boolean };
	unsafeMetadata: { admin?: boolean };
	role?: string | null;
};

type AuthOrganization = {
	id: string;
	name: string;
	slug?: string | null;
	imageUrl?: string | null;
};

function splitName(name: string) {
	const trimmed = name.trim();
	if (!trimmed) {
		return { firstName: null, lastName: null };
	}

	const [first, ...rest] = trimmed.split(/\s+/);
	return {
		firstName: first ?? null,
		lastName: rest.length > 0 ? rest.join(" ") : null,
	};
}

function toAuthUser(
	user: {
		id: string;
		name: string;
		email: string;
		image?: string | null;
		role?: string | null;
		publicMetadata?: { admin?: unknown };
		privateMetadata?: { admin?: unknown };
		unsafeMetadata?: { admin?: unknown };
	} | null,
): AuthUser | null {
	if (!user) {
		return null;
	}

	const { firstName, lastName } = splitName(user.name);
	const hasPublicAdminFlag = isSuperAdminFlag(user.publicMetadata?.admin);
	const hasPrivateAdminFlag = isSuperAdminFlag(user.privateMetadata?.admin);
	const hasUnsafeAdminFlag = isSuperAdminFlag(user.unsafeMetadata?.admin);
	const isSuperAdmin =
		isSuperAdminRole(user.role) ||
		hasPublicAdminFlag ||
		hasPrivateAdminFlag ||
		hasUnsafeAdminFlag;

	return {
		id: user.id,
		fullName: user.name,
		firstName,
		lastName,
		emailAddresses: [{ emailAddress: user.email }],
		imageUrl: user.image ?? null,
		publicMetadata: {
			admin: hasPublicAdminFlag || isSuperAdmin,
		},
		privateMetadata: {
			admin: hasPrivateAdminFlag || isSuperAdmin,
		},
		unsafeMetadata: {
			admin: hasUnsafeAdminFlag || isSuperAdmin,
		},
		role: user.role ?? null,
	};
}

function initialsFromName(name: string | undefined) {
	if (!name) {
		return "U";
	}

	const [first, second] = name
		.split(" ")
		.filter(Boolean)
		.map((part) => part[0]?.toUpperCase() ?? "");

	return `${first ?? ""}${second ?? ""}` || "U";
}

function slugifyOrganizationName(name: string) {
	return name
		.trim()
		.toLowerCase()
		.replace(/[^a-z0-9]+/g, "-")
		.replace(/^-+|-+$/g, "")
		.slice(0, 48);
}

export function useAuth() {
	const sessionState = authClient.useSession();

	return {
		isLoaded: !sessionState.isPending,
		isSignedIn: Boolean(sessionState.data?.user),
		userId: sessionState.data?.user?.id ?? null,
		orgId: sessionState.data?.session?.activeOrganizationId ?? null,
		getToken: async () => null,
	};
}

export function useUser() {
	const sessionState = authClient.useSession();
	const user = toAuthUser(sessionState.data?.user ?? null);

	return {
		isLoaded: !sessionState.isPending,
		isSignedIn: Boolean(user),
		user,
	};
}

export function useOrganization() {
	const activeOrganizationState = authClient.useActiveOrganization();
	const organization = activeOrganizationState.data
		? {
				id: activeOrganizationState.data.id,
				name: activeOrganizationState.data.name,
				slug: activeOrganizationState.data.slug,
				imageUrl: activeOrganizationState.data.logo ?? null,
			}
		: null;

	return {
		organization,
	};
}

type UseOrganizationListOptions = {
	userMemberships?: {
		infinite?: boolean;
	};
};

export function useOrganizationList(_options?: UseOrganizationListOptions) {
	const listOrganizationsState = authClient.useListOrganizations();

	const memberships =
		listOrganizationsState.data?.map((organization) => ({
			organization: {
				id: organization.id,
				name: organization.name,
				slug: organization.slug,
				imageUrl: organization.logo ?? null,
			},
			role: null,
		})) ?? [];

	const setActiveOrganization = async ({
		organizationId,
	}: {
		organizationId: string | null;
	}) => {
		const response = await authClient.organization.setActive({
			organizationId,
		});

		if (response.error) {
			throw new Error(response.error.message ?? "Failed to set organization");
		}

		return response.data;
	};

	const createOrganization = async ({
		name,
	}: {
		name: string;
	}): Promise<AuthOrganization> => {
		const slug = slugifyOrganizationName(name);
		const response = await authClient.organization.create({
			name,
			slug,
		});

		if (response.error || !response.data) {
			throw new Error(
				response.error?.message ?? "Failed to create organization",
			);
		}

		return {
			id: response.data.id,
			name: response.data.name,
			slug: response.data.slug,
			imageUrl: response.data.logo ?? null,
		};
	};

	return {
		isLoaded: !listOrganizationsState.isPending,
		userMemberships: {
			data: memberships,
		},
		setActiveOrganization,
		createOrganization,
	};
}

export function useAuthActions() {
	const navigate = useNavigate();

	return {
		openUserProfile: () => {
			void navigate({ to: "/account/settings" });
		},
		openOrganizationProfile: () => {
			void navigate({ to: "/organization/settings" });
		},
		signOut: async (options?: { redirectUrl?: string }) => {
			await authClient.signOut();
			if (options?.redirectUrl) {
				void navigate({ href: options.redirectUrl });
			}
		},
	};
}

export function SignedIn({ children }: { children: ReactNode }) {
	const { isSignedIn } = useAuth();
	if (!isSignedIn) {
		return null;
	}
	return <>{children}</>;
}

export function SignedOut({ children }: { children: ReactNode }) {
	const { isSignedIn } = useAuth();
	if (isSignedIn) {
		return null;
	}
	return <>{children}</>;
}

export function RedirectToSignIn() {
	return <Navigate to="/sign-in" />;
}

type UserButtonProps = {
	appearance?: {
		elements?: {
			avatarBox?: string;
		};
	};
};

export function UserButton({ appearance }: UserButtonProps = {}) {
	const { user } = useUser();

	return (
		<Avatar className={appearance?.elements?.avatarBox ?? "h-8 w-8"}>
			<AvatarImage alt={user?.fullName ?? "User"} src={user?.imageUrl ?? ""} />
			<AvatarFallback>{initialsFromName(user?.fullName)}</AvatarFallback>
		</Avatar>
	);
}
